import _ from 'lodash'
import type {ComponentWithResolvedDataItems} from '@wix/document-services-types'
import type {TraverseOptions, InnerElementContext} from './types'
import {getFeatureObjectValue, INNER_ELEMENTS_FEATURE} from './utils'
import {isInnerElementsMapWrapper} from '../innerElementsMap/common'

const INNER_ELEMENT_TYPE = 'InnerElement'

/**
 * Unwrap InnerElementsMap wrapper if present
 * Returns the actual innerElements object, handling both wrapped and unwrapped formats
 */
const unwrapInnerElements = (innerElements: unknown): Record<string, unknown> | null => {
    if (!innerElements || !_.isPlainObject(innerElements)) {
        return null
    }

    // If wrapped in InnerElementsMap, access the actual innerElements inside
    if (isInnerElementsMapWrapper(innerElements)) {
        return (innerElements as {innerElements: Record<string, unknown>}).innerElements
    }

    return innerElements as Record<string, unknown>
}

/**
 * Get nested innerElements from an element
 * Uses COMPONENT_FEATURES to check both query and namespace property names
 * Handles InnerElementsMap wrappers
 */
const getNestedInnerElements = (element: Record<string, unknown>): Record<string, unknown> | null => {
    const result = getFeatureObjectValue(element, INNER_ELEMENTS_FEATURE)
    if (!result) {
        return null
    }
    return unwrapInnerElements(result.value)
}

/**
 * Recursively process innerElements and call transformation for each
 */
const processInnerElements = (
    innerElements: Record<string, unknown>,
    component: ComponentWithResolvedDataItems,
    componentType: string,
    currentPath: string[],
    onInnerElement: (ctx: InnerElementContext) => void
): void => {
    for (const key of Object.keys(innerElements)) {
        const element = innerElements[key] as Record<string, unknown>
        if (!element || !_.isPlainObject(element)) {
            continue
        }

        const elementPath = [...currentPath, key]

        // Call transformation for this innerElement
        if (element.type === INNER_ELEMENT_TYPE) {
            onInnerElement({
                component,
                componentType,
                element,
                elementKey: key,
                path: elementPath
            })
        }

        // Recursively process nested innerElements (supports both query and namespace property names)
        const nested = getNestedInnerElements(element)
        if (nested) {
            processInnerElements(nested, component, componentType, elementPath, onInnerElement)
        }
    }
}

/**
 * Traverse component and apply transformations efficiently in a single pass
 * Works with nested format: { structure: {...}, data: {...} }
 * Handles InnerElementsMap wrappers since beautifier runs before innerElementsMapPlugin
 */
export const traverseForPack = (component: ComponentWithResolvedDataItems, options: TraverseOptions): void => {
    const componentType = (component.structure?.componentType as string) || ''
    const {onComponent, onInnerElement} = options
    const innerElements = unwrapInnerElements(component.data?.innerElements)

    // Process innerElements first (children before parent for pack)
    if (onInnerElement && innerElements) {
        processInnerElements(innerElements, component, componentType, [], onInnerElement)
    }

    // Then process component-level data
    if (onComponent) {
        onComponent({component, componentType})
    }
}

/**
 * Recursively process innerElements in reverse order for unpack
 */
const processInnerElementsReverse = (
    innerElements: Record<string, unknown>,
    component: ComponentWithResolvedDataItems,
    componentType: string,
    currentPath: string[],
    onInnerElement: (ctx: InnerElementContext) => void
): void => {
    for (const key of Object.keys(innerElements)) {
        const element = innerElements[key] as Record<string, unknown>
        if (!element || !_.isPlainObject(element)) {
            continue
        }

        const elementPath = [...currentPath, key]

        // Recursively process nested innerElements first (supports both query and namespace property names)
        const nested = getNestedInnerElements(element)
        if (nested) {
            processInnerElementsReverse(nested, component, componentType, elementPath, onInnerElement)
        }

        // Then call transformation for this innerElement
        if (element.type === INNER_ELEMENT_TYPE) {
            onInnerElement({
                component,
                componentType,
                element,
                elementKey: key,
                path: elementPath
            })
        }
    }
}

/**
 * Traverse component and apply transformations in reverse order for unpack
 * Works with nested format: { structure: {...}, data: {...} }
 * Handles InnerElementsMap wrappers since beautifier runs before innerElementsMapPlugin
 */
export const traverseForUnpack = (component: ComponentWithResolvedDataItems, options: TraverseOptions): void => {
    const componentType = (component.structure?.componentType as string) || ''
    const {onComponent, onInnerElement} = options
    const innerElements = unwrapInnerElements(component.data?.innerElements)

    // Process innerElements first in reverse order (deepest children first)
    if (onInnerElement && innerElements) {
        processInnerElementsReverse(innerElements, component, componentType, [], onInnerElement)
    }

    // Then process component-level data
    if (onComponent) {
        onComponent({component, componentType})
    }
}
